@let isInFullscreenMode = isInFullscreenMode$ | async;

<div class="cc-awesome-stage__root">
  <ng-container *ngTemplateOutlet="exteriorImageLoadingSpinner" />
  <ng-container *ngTemplateOutlet="returnToConfigurationButton" />
  <ng-container *ngTemplateOutlet="viewToggle" />
  <ng-container *ngTemplateOutlet="fullscreenCloseButton" />

  <div class="cc-awesome-stage__container">
    <ng-container *ngTemplateOutlet="stagePlaceholder" />

    @if ((hasImageError$ | async) === false) {
      <div [class.cc-awesome-stage__exterior--display-none]="(isExteriorView$ | async) === false">
        <ng-container *ngTemplateOutlet="exteriorView" />
      </div>

      @if (isInteriorThreeSixtyView$ | async) {
        <ng-container *ngTemplateOutlet="interiorThreeSixtyView" />
      }

      @if (isInteriorView$ | async) {
        <ng-container *ngTemplateOutlet="interiorView" />
      }

      <ng-container *ngTemplateOutlet="controlBarTemplate" />
    } @else {
      <ng-container *ngTemplateOutlet="showFallbackImage" />
    }
  </div>
  <cc-lazy-host [componentId]="(isInScreenMirroringMode$ | async) ? screenMirroringComponent : null" />
</div>

<ng-template #showFallbackImage>
  <div class="cc-awesome-stage__exterior-view">
    <cc-image [src]="currentFallbackImage$ | async" />
  </div>
</ng-template>

<ng-template #exteriorImageLoadingSpinner>
  @if ((isStageRotationIndicatorVersionBActive$ | async) === false) {
    <ccwb-spinner
      class="cc-awesome-stage__spinner"
      [style.opacity]="(showExteriorThreeSixtySpinner$ | async) ? 1 : 0"
      theme="light"
    />
  }
</ng-template>

<ng-template #returnToConfigurationButton>
  @if ((isSummary$ | async) && isInFullscreenMode === false) {
    <div class="cc-awesome-stage__return-to-configuration">
      <ccwb-button
        class="cc-awesome-stage__return-to-configuration--button"
        [ccTrackClickOnce]="summarySidebarTrackingData"
        [size]="(currentMq$ | async) === 'mq3-max' ? 's' : 'm'"
        (click)="onClickReturnToConfiguration($event)"
        variant="translucent"
      >
        <ccwb-icon name="bds/chevron-left/24" />
        <span class="cc-awesome-stage__return-to-configuration--text">{{
          'summary.sidebar.returnToConfiguration' | translate
        }}</span>
      </ccwb-button>
    </div>
  }
</ng-template>

<ng-template #viewToggle>
  @if (displayStageViewToggle$ | async) {
    <div class="cc-awesome-stage__view-toggle" [class.cc-awesome-stage__view-toggle--fullscreen]="isInFullscreenMode">
      <ccwb-tooltip position="end">
        <img
          class="cc-awesome-stage__view-toggle-img"
          [src]="stageViewTogglePreviewImage$ | async"
          [ccTrackClickOnce]="stageViewTrackingData$ | async"
          (click)="toggleView()"
          (keyup.enter)="toggleView()"
          alt="view toggle"
          tabindex="0"
          ccDoubleClickStopPropagation
        />

        <div slot="content">{{ stageViewToggleButtonTooltip$ | async }}</div>
      </ccwb-tooltip>
    </div>
  }
</ng-template>

<ng-template #fullscreenCloseButton>
  @if (displayFullscreenCloseButton$ | async) {
    <ccwb-round-button
      class="cc-awesome-stage__fullscreen-close-button"
      (click)="toggleFullscreenModeState()"
      elevation="xs"
      size="m"
      theme="dark"
      ccDoubleClickStopPropagation
    >
      <ccwb-icon name="close" aria-hidden="true" />
    </ccwb-round-button>
  }
</ng-template>

<!-- placeholder which reserves the height for the views -->
<ng-template #stagePlaceholder>
  <div #stageContainer [style.paddingBottom]="stagePadding$ | async"></div>
</ng-template>

<ng-template #exteriorView>
  <!--
    ℹ️ it is important to have set the 'currentIndex' _above_ the 'images' (to prevent unwanted behavior). Reason is the order of execution.
    Details: https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/3192
  -->

  <ccwb-360-viewer
    class="cc-awesome-stage__exterior-view"
    [@inOutAnimation]
    [id]="exteriorThreeSixtyComponentId"
    [rotationSpeedFactor]="exteriorThreeSixtyRotationSpeedFactor$ | async"
    [currentIndex]="exteriorThreeSixtyIndex$ | async"
    [images]="exteriorImages$ | async"
    [ratio]="exteriorViewRatio"
    [style.transform]="exteriorTransformStyle$ | async"
    [style.paddingTop]="stagePadding$ | async"
    [ccTrackClickOnce]="stageExteriorThreeSixtyInteractionTrackingData$ | async"
    (wbchange)="onExteriorThreeSixtyRotation($event)"
  >
    @if (isInFullscreenMode !== true) {
      <ccwb-icon
        class="cc-awesome-stage__exterior-view-360-indicator"
        [style.transform]="exteriorViewThreeSixtyIndicatorTransformStyle$ | async"
        src="{{ stageSliderIconSrc }}"
      />
    }
  </ccwb-360-viewer>
</ng-template>

<ng-template #interiorThreeSixtyView>
  <div
    class="cc-awesome-stage__interior-three-sixty-view"
    [id]="interiorThreeSixtyContainerId"
    [ccTrackClickOnce]="stageInteriorThreeSixtyInteractionTrackingData$ | async"
  >
    @if (showInteriorThreeSixtySpinner$ | async) {
      <ccwb-spinner class="cc-awesome-stage__interior-three-sixty-view--spinner" theme="light" />
    }
  </div>
</ng-template>

<ng-template #interiorView>
  <!-- ℹ️ item = index + 1 -->
  <ccwb-slider
    class="cc-awesome-stage__interior-view"
    #interiorSlider
    [@inOutAnimation]
    [initialItem]="(interiorSliderIndex$ | async) + 1"
    (wbchange)="onInteriorChange($event)"
    show-arrow-navigation
    scroll-snap
  >
    @for (image of interiorImages$ | async; track $index) {
      <ccwb-slider-item class="slider-item">
        <cc-image [src]="image" [isFullScreen]="isInFullscreenMode" />
      </ccwb-slider-item>
    }
  </ccwb-slider>
</ng-template>

<ng-template #controlBarTemplate>
  <ng-container *ngrxLet="isStageActionsHidden$ as isStageActionsHidden">
    @if (!isStageActionsHidden) {
      <div class="cc-awesome-stage__control-bar" #controlBar>
        @let disclaimer = disclaimer$ | async;
        @if (disclaimer) {
          <ccwb-text
            class="cc-awesome-stage__control-bar-disclaimer"
            [style.color]="disclaimerColorStyle$ | async"
            size="s"
            static
            tag="span"
            >{{ disclaimer }}</ccwb-text
          >
        }
        <ccwb-button-group
          class="cc-awesome-stage__control-bar-button-group"
          [class.cc-awesome-stage__control-bar-button-group--fullscreen]="isInFullscreenMode"
        >
          @if ((hasInteriorThreeSixtyView$ | async) && (threeSixtyChangeForCurrentViewOffered$ | async)) {
            <!-- interior 360 toggle button -->
            <cc-optional-tooltip-wrapper
              class="cc-awesome-stage__control-bar-button-wrapper"
              @buttonWrapperAnimation
              [displayTooltip]="!!(threeSixtyChangeForCurrentViewOffered$ | async)"
            >
              <ccwb-round-button
                [disabled]="!!!(threeSixtyChangeForCurrentViewOffered$ | async)"
                [ccTrackClickOnce]="interiorThreeSixtyStateTrackingData$ | async"
                (click)="toggleInteriorThreeSixty()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon
                  class="stage-interaction-icon cc-awesome-stage__control-bar-button-three-sixty"
                  name="{{ interiorThreeSixtyStateIcon$ | async }}"
                />
              </ccwb-round-button>

              <div slot="content">{{ interiorThreeSixtyStateButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }

          <!-- only interior view: (go to) trunk button -->
          @if (displayTrunkButtonOffered$ | async) {
            <cc-optional-tooltip-wrapper
              class="cc-awesome-stage__control-bar-button-wrapper"
              @buttonWrapperAnimation
              [displayTooltip]="!!(displayTrunkButtonOffered$ | async)"
            >
              <ccwb-round-button
                [disabled]="!!!(displayTrunkButtonEnabled$ | async)"
                [ccTrackClickOnce]="trunkButtonTrackingData$ | async"
                (click)="slideToTrunkImage()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon class="stage-interaction-icon">
                  <ng-container *ngTemplateOutlet="trunkSvg" />
                </ccwb-icon>
              </ccwb-round-button>

              <div slot="content">{{ trunkButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }

          <!-- stage view state toggle button (day / night) -->
          @if (
            (stageViewStateChangeOfferedInAnyView$ | async) && (stageViewStateChangeForCurrentViewOffered$ | async)
          ) {
            <cc-optional-tooltip-wrapper
              class="cc-awesome-stage__control-bar-button-wrapper"
              @buttonWrapperAnimation
              [displayTooltip]="!!(stageViewStateChangeForCurrentViewOffered$ | async)"
            >
              <ccwb-round-button
                [disabled]="!!!(stageViewStateChangeForCurrentViewOffered$ | async)"
                [ccTrackClickOnce]="stageViewStateTrackingData$ | async"
                (click)="toggleStageViewState()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon class="stage-interaction-icon" name="{{ stageViewStateIcon$ | async }}" />
              </ccwb-round-button>

              <div slot="content">{{ stageViewStateButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }

          <!-- roof state toggle button (opened / closed) -->
          @if ((roofStateChangeOfferedInAnyView$ | async) && (roofStateChangeForCurrentViewOffered$ | async)) {
            <cc-optional-tooltip-wrapper
              class="cc-awesome-stage__control-bar-button-wrapper"
              @buttonWrapperAnimation
              [displayTooltip]="!!(roofStateChangeForCurrentViewOffered$ | async)"
            >
              <ccwb-round-button
                [disabled]="!!!(roofStateChangeForCurrentViewOffered$ | async)"
                [ccTrackClickOnce]="roofStateTrackingData$ | async"
                (click)="toggleRoofState()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon
                  class="stage-interaction-icon cc-awesome-stage__control-bar-button-roof-state"
                  name="{{ roofStateIcon$ | async }}"
                />
              </ccwb-round-button>

              <div slot="content">{{ roofStateButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }

          <!-- "oneMirror" screen mirroring button -->
          @if ((isUserEmployeeAndIsScreenMirroringActive$ | async) || !!(devUserAccessToken$ | async)) {
            <cc-optional-tooltip-wrapper class="cc-awesome-stage__control-bar-button-wrapper">
              <ccwb-round-button
                [ccTrackClickOnce]="screenMirrorTrackingData$ | async"
                (click)="toggleScreenMirroringMode()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon class="stage-interaction-icon" [name]="screenMirroringStateIcon$ | async" />
              </ccwb-round-button>

              <div slot="content">{{ screenMirroringButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }

          <!-- fullscreen mode toggle button -->
          @if (isFullscreenSupported$ | async) {
            <cc-optional-tooltip-wrapper
              class="cc-awesome-stage__control-bar-button-wrapper"
              @buttonWrapperAnimation
              [displayTooltip]="!!(isFullscreenSupported$ | async)"
            >
              <ccwb-round-button
                [disabled]="!!!(isFullscreenSupported$ | async)"
                [ccTrackClickOnce]="fullscreenModeTrackingData$ | async"
                (click)="toggleFullscreenModeState()"
                size="m"
                elevation="xs"
                ccDoubleClickStopPropagation
                ccTouchStartStopPropagation
                ccPreventTouchStartWhenDisabled
              >
                <ccwb-icon class="stage-interaction-icon" name="{{ fullscreenModeStateIcon$ | async }}" />
              </ccwb-round-button>

              <div slot="content">{{ fullscreenButtonTooltip$ | async }}</div>
            </cc-optional-tooltip-wrapper>
          }
        </ccwb-button-group>
      </div>
    }
  </ng-container>
</ng-template>

<!-- #pause-sonar# -->
<ng-template #trunkSvg>
  <svg
    width="20px"
    height="19px"
    viewBox="0 0 20 19"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
  >
    <title>Trunk icon</title>
    <defs>
      <rect id="path-1" x="0" y="0" width="20" height="20" />
    </defs>
    <g id="icon" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
      <g id="Artboard" transform="translate(-22.000000, -23.000000)">
        <g id="Round-Button-/-theme-light-/-S-/-elevation-xs" transform="translate(8.000000, 8.000000)">
          <g id="Trunk-icon" transform="translate(12.000000, 12.000000)">
            <rect id="Rectangle" x="0" y="0" width="24" height="24" />
            <g id="trunk-open" transform="translate(2.000000, 2.000000)">
              <mask id="mask-2" fill="white">
                <use xlink:href="#path-1" />
              </mask>
              <g id="Mask" />
              <g mask="url(#mask-2)">
                <g transform="translate(-37.000000, 2.000000)">
                  <path
                    id="Shape1"
                    d="M9.36,10.8 C7.35370487,10.8 5.76,12.3937049 5.76,14.4 C5.76,16.4062951 7.35370487,18 9.36,18 C11.3662951,18 12.96,16.4062951 12.96,14.4 C12.9006777,12.3937049 11.3076393,10.8 9.36,10.8 Z M9.36,16.642918 C8.12089613,16.642918 7.05842622,15.6397704 7.05842622,14.3413442 C7.05842622,13.1022403 8.06157378,12.0397704 9.36,12.0397704 C10.6584262,12.0397704 11.6615738,13.042918 11.6615738,14.3413442 C11.602918,15.6391039 10.5997704,16.642918 9.36,16.642918 L9.36,16.642918 Z"
                    stroke="none"
                    fill="#000000"
                    fill-rule="nonzero"
                  />
                  <path
                    id="Shape2"
                    d="M54.6293238,6.57474282 L49.8430949,4.65804189 C49.8430949,4.65804189 42.0568233,0.760413241 40.5258067,0.569176136 C35.4844085,-0.069483623 29.1022895,-0.325669176 24.6346621,0.633402936 C24.6346621,0.633402936 22.5284331,1.39979465 16.593221,5.5528872 C16.593221,5.5528872 6.89245783,6.19154696 3.3186442,7.53381489 L1.85105958,8.1089695 L0.319322194,10.2811343 L0.446906908,11.8146394 L0,12.3255672 C0,12.3255672 0.319322194,15.1371134 0.255169428,15.1371134 L4.33932191,15.84 L4.46690663,15.84 C4.34065533,15.3820675 4.27617075,14.9092821 4.27516915,14.4342269 C4.27516915,11.8146394 6.74343214,9.44979417 9.36,9.44979417 C11.9765679,9.44979417 14.4411418,11.8146394 14.4411418,14.4342269 C14.4411418,14.9451547 14.3886229,15.3925773 14.2610382,15.84 L40.5258067,15.84 C40.3953039,15.3829516 40.3257126,14.9095715 40.3290437,14.4342269 C40.3290437,13.3267121 40.6201633,12.1352942 41.316105,11.2783077 C42.2662632,10.1082774 43.8496703,9.44979417 45.36,9.44979417 C46.522168,9.44979417 47.7360043,9.82387671 48.6743915,10.5720398 C49.84874,11.5083315 50.4623252,12.9781486 50.4623252,14.4342269 C50.4623252,14.7539176 50.526478,15.0093815 50.4623252,15.2648454 L55.8402973,14.3700001 L56.1600022,10.2169075 C56.1610611,10.2169075 55.778307,9.89721682 55.2672473,9.44979417 L54.6293238,6.57474282 Z M3.77157612,9.9859332 L1.44,10.8 C1.44,10.8 1.9202582,9.29705305 3.01710597,8.67111984 C3.63369554,8.35779961 5.76,7.92 5.76,7.92 L3.77157612,9.9859332 L3.77157612,9.9859332 Z M46.387758,6.11164741 C41.4787991,6.59547219 35.3913943,6.47451599 24.0675203,7.01916051 C19.9436991,7.20093649 16.6708134,7.32189268 16.1473883,7.38202909 C13.9220922,7.56380507 9.47076063,7.62394148 8.0964,7.80571746 C9.79827099,7.20093649 13.9220922,6.83738457 17.2600364,6.71642838 C20.9912887,4.17498158 24.3292329,2.23899911 25.3110247,1.99708673 C26.3586142,1.69435456 28.6489689,1.27134956 31.7259398,1.33148598 L31.2675731,6.17178382 C31.9218545,6.17178382 33.1660982,6.11096404 33.2311567,6.11096404 L33.6895233,1.45244217 C35.5222506,1.51326195 39.580274,1.69435456 41.4130013,2.05722314 L41.0854909,5.86905166 C41.282145,5.86905166 42.1330805,5.86905166 42.4598515,5.80823188 L42.7873619,2.35927194 C46.2561624,3.81142963 48.4164,5.98932448 48.4164,5.98932448 C48.4164,5.93055481 47.6312623,5.99069122 46.387758,6.11164741 Z"
                    stroke="none"
                    fill="#000000"
                    fill-rule="nonzero"
                  />
                  <path
                    id="Shape3"
                    d="M45.36,10.8 C43.3537049,10.8 41.76,12.3937049 41.76,14.4 C41.76,16.4062951 43.3537049,18 45.36,18 C47.3662951,18 48.96,16.4062951 48.96,14.4 C48.96,12.3937049 47.3662951,10.8 45.36,10.8 L45.36,10.8 Z M45.36,16.642918 C44.1208961,16.642918 43.0584262,15.6397704 43.0584262,14.3413442 C43.0584262,13.1022403 44.0615738,12.0397704 45.36,12.0397704 C46.5991039,12.0397704 47.6615738,13.042918 47.6615738,14.3413442 C47.6615738,15.6391039 46.5991039,16.642918 45.36,16.642918 L45.36,16.642918 Z"
                    stroke="none"
                    fill="#000000"
                    fill-rule="nonzero"
                  />
                  <polyline
                    id="Path-2"
                    stroke="#000000"
                    stroke-width="1.5"
                    fill="none"
                    points="48.96 5.76 53.3009564 0 56.16 0"
                  />
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
</ng-template>
<!-- #continue-sonar# -->
